/* eslint-disable no-restricted-syntax */
/* eslint-disable max-len */

import { TreeOptionSelections } from '../../modelConfiguration/data/optionsSelection';
import { ModelGroupInfo } from './modelGroupInfo';
import { SLTXFormatRootFile, SLTXFormatRootFileVariantComposition } from './sltx';

export class SLTXUtil {

  // This method will return a default configuration for a model
  // so it can be presented to the user. Rules are explained inside
  // the method.
  public static getDefaultComposition(
    sltxContent: SLTXFormatRootFile,
  ) : TreeOptionSelections {

    // If the model have variants, pick the composition of the first one
    // If there is no variants, select the first option of each group
    // If there is no groups, leave the array empty

    const { variants, groups } = sltxContent;

    // TODO PMC: Maybe we may want to select the "Dummy variant"
    if (variants !== undefined && variants.length > 0) {
      return [...variants[0].variantComposition].map((composition) => ({
        groupID: composition.groupId,
        optionID: composition.optionId,
      }));
    }

    if (groups !== undefined) {

      const newComposition = groups.map((group) => {

        const groupOptions = ModelGroupInfo.getGroupAllOptions(group);

        let optionID = -1;
        if (groupOptions.length > 0) {
          optionID = groupOptions[0].id;
        }

        return {
          groupID: group.id,
          optionID,
        };
      });

      return newComposition;
    }

    return [];
  }

  // Checks if the 2 object compositions are the same
  public static compositionsMatch(
    compositionA : SLTXFormatRootFileVariantComposition[],
    compositionB : SLTXFormatRootFileVariantComposition[],
  ) : boolean {

    if (compositionA.length !== compositionB.length) {
      return false;
    }

    const numOptions = compositionA.length;

    for (let curOption = 0; curOption < numOptions; curOption += 1) {
      const optionA = compositionA[curOption];

      const match = compositionB.some((optionB) => optionB.groupId === optionA.groupId
                                                && optionB.optionId === optionA.optionId);

      if (match === false) {
        return false;
      }
    }

    return true;
  }

  public static compositionsMatch2(
    compositionA : TreeOptionSelections,
    compositionB : TreeOptionSelections,
  ) : boolean {

    if (compositionA.length !== compositionB.length) {
      return false;
    }

    const numOptions = compositionA.length;

    for (let curOption = 0; curOption < numOptions; curOption += 1) {
      const optionA = compositionA[curOption];

      const match = compositionB.some((optionB) => optionB.groupID === optionA.groupID
                                                && optionB.optionID === optionA.optionID);

      if (match === false) {
        return false;
      }
    }

    return true;
  }

  // Checks id a specific group option is being used by
  // the model composition
  public static isGroupOptionInComposition(
    groupID : number,
    groupOptionID : number,
    composition : TreeOptionSelections,
  ) : boolean {

    return composition.some((compositionOption) => compositionOption.groupID === groupID
                                                && compositionOption.optionID === groupOptionID);

  }

  // To support mapping of groupID,optionID to groupName, folder+optionNames (and vice versa)
  public static getGroupAndOptionNamesPair(
    sltxContent: SLTXFormatRootFile,
    groupId: number,
    optionId: number,
  ): [string, string] | undefined {

    const group = sltxContent.groups?.find((g) => g.id === groupId);
    if (!group) {
      return undefined;
    }

    const folderWithOption = group.optionsFolders.find((f) => f.options.some((o) => o.id === optionId));

    if (!folderWithOption) {
      return undefined;
    }

    const option = folderWithOption.options.find((opt) => opt.id === optionId);
    if (!option) {
      return undefined;
    }

    const groupName = group.name;
    const folderOptionName = `${folderWithOption.name}+${option.name}`;

    return [groupName, folderOptionName];
  }

  public static getGroupAndOptionIdsPair(
    sltxContent: SLTXFormatRootFile,
    groupName: string,
    folderOptionName: string,
  ): [number, number] | undefined {

    const [folderName, optionName] = folderOptionName.split('+');
    if (!folderName || !optionName) {
      return undefined;
    }

    const group = sltxContent.groups?.find((g) => g.name === groupName);
    if (!group) {
      return undefined;
    }

    const folder = group.optionsFolders.find((f) => f.name === folderName);
    if (!folder) {
      return undefined;
    }

    const option = folder.options.find((o) => o.name === optionName);
    if (!option) {
      return undefined;
    }

    return [group.id, option.id];
  }

  // ---------------------------------------------------------------
  // Find group names matching a regex or a specific string
  // ---------------------------------------------------------------
  public static getGroupsMatching(
    sltxContent: SLTXFormatRootFile,
    regex: RegExp | string,
  ): number[] {

    const matcher = typeof regex === 'string' ? new RegExp(regex) : regex;

    return sltxContent.groups
      ?.filter((group) => matcher.test(group.name))
      .map((group) => group.id) ?? [];
  }

  // ---------------------------------------------------------------
  // Find all domain names based on thee existing group names
  // (considering the convention that group names are
  // in the format "DomainName=EffectiveGroupName")
  // ---------------------------------------------------------------
  public static getAllCandidateDomainNames(
    sltxContent: SLTXFormatRootFile,
  ): string[] {

    const domainNames = new Set<string>();

    sltxContent.groups?.forEach((group) => {
      const separatorIndex = group.name.indexOf('=');

      if (separatorIndex > 0) {
        const domainName = group.name.substring(0, separatorIndex).trim();

        if (domainName.length > 0) {
          domainNames.add(domainName);
        }
      }
    });

    return [...domainNames];
  }

  // -------------------------------------------------------------
  // Get the ids of all groups belonging to a specific domain
  // (considering the convention that group names are in
  // the format "DomainName=EffectiveGroupName")
  // -------------------------------------------------------------
  public static getAllGroupsFromDomain(
    sltxContent: SLTXFormatRootFile,
    domainName: string,
  ): number[] {

    const domainPrefix = `${domainName}=`;

    return sltxContent.groups
      ?.filter((group) => group.name.startsWith(domainPrefix))
      .map((group) => group.id) ?? [];
  }

  // -------------------------------------------------------------
  // Get all distinct folder names for a list of groups
  // -------------------------------------------------------------
  public static getGroupsFolders(
    sltxContent: SLTXFormatRootFile,
    groupIds: number[],
  ): string[] {

    const folders = new Set<string>();

    groupIds.forEach((groupId) => {
      const group = sltxContent.groups?.find((g) => g.id === groupId);

      if (group !== undefined) {
        group.optionsFolders.forEach((folder) => {
          folders.add(folder.name);
        });
      }
    });

    return [...folders];
  }

  // -------------------------------------------------------------
  // Get all folder names for a specific group
  // -------------------------------------------------------------
  public static getGroupFolders(
    sltxContent: SLTXFormatRootFile,
    groupId: number,
  ): string[] {

    const group = sltxContent.groups?.find((g) => g.id === groupId);

    if (group === undefined) {
      return [];
    }

    return group.optionsFolders.map((folder) => folder.name);
  }

  // -------------------------------------------------------------
  // Get all option ids for a specific folder within a group
  // -------------------------------------------------------------
  public static getGroupFolderOptions(
    sltxContent: SLTXFormatRootFile,
    groupId: number,
    folderName: string,
  ): number[] {

    const group = sltxContent.groups?.find((g) => g.id === groupId);

    if (group === undefined) {
      return [];
    }

    const folder = group.optionsFolders.find((f) => f.name === folderName);

    if (folder === undefined) {
      return [];
    }

    return folder.options.map((option) => option.id);
  }

  // Special Names and Chars...
  public static DummyOption = 'Reiniciar Reset';
  public static OptionSectionSeparator = '.';
}
